import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./i18n/LanguageContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import PhoneLoginPage from "./pages/PhoneLoginPage";
import DashboardPage from "./pages/DashboardPage";
import ScanDetailPage from "./pages/ScanDetailPage";
import HistoryPage from "./pages/HistoryPage";
import ProfilePage from "./pages/ProfilePage";
import FieldsPage from "./pages/FieldsPage";
import FieldDetailPage from "./pages/FieldDetailPage";
import AdminPage from "./pages/AdminPage";
import ChemicalCheckPage from "./pages/ChemicalCheckPage";
import DownloadPage from "./pages/DownloadPage";
import SoilReportPage from "./pages/SoilReportPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import { InstallBanner } from "./components/InstallBanner";
import { Loader2 } from "lucide-react";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-6 h-6 animate-spin text-[#52796F]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors />
            <InstallBanner />
            <Routes>
              <Route path="/" element={<LandingPage />} />
              {/* Mobile OTP is the preferred login flow */}
              <Route path="/login" element={<PhoneLoginPage />} />
              <Route path="/login/phone" element={<Navigate to="/login" replace />} />
              <Route path="/login/email" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/download" element={<DownloadPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <DashboardPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/scan/:id"
                element={
                  <RequireAuth>
                    <ScanDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/history"
                element={
                  <RequireAuth>
                    <HistoryPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/fields"
                element={
                  <RequireAuth>
                    <FieldsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/fields/:id"
                element={
                  <RequireAuth>
                    <FieldDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/profile"
                element={
                  <RequireAuth>
                    <ProfilePage />
                  </RequireAuth>
                }
              />
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/chemical-check"
                element={
                  <RequireAuth>
                    <ChemicalCheckPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/soil-report"
                element={
                  <RequireAuth>
                    <SoilReportPage />
                  </RequireAuth>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
